import { Text as UIKText, TextProps } from "@ui-kitten/components";
import RenderHtmlComponent from "components/RenderHtml";
import { BRANDING_URL } from "constants/";
import { selectThemeColor } from "features/slices/tenantSlice";
import { TicketDetails } from "features/types";
import { useAppSelector } from "hooks/useAppSelector";
import React, { useState } from "react";
import { View, StyleSheet } from "react-native";
import ticketFooterTexts from "../ticketFooterTexts";
import BarcodeImageBackground from "./BarcodeImageBackground";
import i18n from "plugins/localization";
import { Image } from "expo-image";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import Button from "components/Button";
import Dialog from "components/Dialog";
import Spacer from "components/Spacer";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { scanTicketsRequest } from "queries/ticket.queries";
import { useAccount } from "hooks/useAccount";
import toast from "utils/toast";
import { formatEventDate } from "utils/eventUtils";
import { getUserName } from "utils/userUtils";

interface BarcodeRenderItemProps {
  ticket: TicketDetails;
  index: number;
  ticketsWithBarcode: TicketDetails[];
}

const CARD_PADDING = 12;
const generateBase64String = (base64: string) =>
  `data:image/png;base64,${base64}`;

const Text = ({ style, children, ...rest }: TextProps) => {
  return (
    <UIKText style={style} status={rest.status || "control"} {...rest}>
      {children}
    </UIKText>
  );
};

const BarcodeRenderItem: React.FC<BarcodeRenderItemProps> = ({
  ticket,
  index,
  ticketsWithBarcode,
}) => {
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const {
    seat: { seatDisplay, sectionDisplay, rowDisplay, ga },
    event: { tenantSubdomain, venueName, name, hostName },
  } = ticket;

  const { fetchAccount } = useAccount();
  const themeColor = useAppSelector(selectThemeColor);
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const ticketFooterText = ticketFooterTexts(tenantSubdomain);

  const { mutateAsync: scanTickets, isLoading } =
    useMutation(scanTicketsRequest);

  const handleScanTicket = async (ticketIds: number[]) => {
    await scanTickets(ticketIds);
    await fetchAccount();
    toast.success("Ticket scanned successfully");
  };

  return (
    <View
      style={{
        display: "flex",
        flexDirection: "row",
        flex: 1,
        alignItems: "center",
      }}
    >
      <View style={{ flex: 1, backgroundColor: themeColor }}>
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            paddingHorizontal: CARD_PADDING,
            paddingTop: 5,
          }}
        >
          <Image
            source={{
              uri: `${BRANDING_URL}/logos/${hostName}_dark.svg`,
            }}
            style={{ width: 50, height: 50 }}
            contentFit="contain"
          ></Image>
          {ticket.event.type !== "EXPERIENCE" && (
            <View style={{ padding: CARD_PADDING }}>
              <Text style={{ textAlign: "right", ...styles.lightText }}>
                {i18n.t("screens.Barcode.eventDate")}
              </Text>
              <Text>{formatEventDate(ticket.event)}</Text>
            </View>
          )}
        </View>
        <BarcodeImageBackground ticket={ticket}></BarcodeImageBackground>
        <View
          style={{
            paddingHorizontal: CARD_PADDING,
            paddingTop: CARD_PADDING,
          }}
        >
          <Text style={styles.lightText}>{venueName}</Text>
          <Text style={{ fontWeight: "700", fontSize: 20 }}>{name}</Text>
        </View>
        {ga && (
          <Text
            style={{
              fontWeight: "500",
              fontSize: 20,
              textAlign: "center",
              marginVertical: 10,
            }}
          >
            {i18n.t("screens.Barcode.generalAdmission")}
          </Text>
        )}
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            paddingHorizontal: CARD_PADDING,
          }}
        >
          <View>
            <Text style={styles.lightText}>{i18n.t("common.section")}</Text>
            <Text style={styles.boldText}>{sectionDisplay}</Text>
          </View>
          <View>
            <Text style={styles.lightText}>{i18n.t("common.row")}</Text>
            <Text style={styles.boldText}>{rowDisplay}</Text>
          </View>
          <View>
            <Text style={styles.lightText}>
              {i18n.t("common.seat", { count: 1 })}
            </Text>
            <Text style={styles.boldText}>{ga ? "GA" : seatDisplay}</Text>
          </View>
        </View>
        <View style={{ marginVertical: 10 }}>
          <Text style={{ ...styles.boldText, textAlign: "center" }}>
            {ticket.user
              ? getUserName(ticket.user)
              : ticket.planPackageComponentName}
          </Text>
        </View>
        <View
          style={{
            alignItems: "center",
            backgroundColor: "white",
            borderWidth: 1,
          }}
        >
          {ticket.scanlessEntry ? (
            <View className="p-4">
              <>
                <Button
                  appearance="outline"
                  disabled={ticket.status === "SCANNED"}
                  onPress={() => setConfirmDialogVisible(true)}
                >
                  {ticket.status === "SCANNED"
                    ? "Ticket Scanned"
                    : "Use Ticket"}
                </Button>
                <Dialog
                  visible={confirmDialogVisible}
                  setVisible={setConfirmDialogVisible}
                  cancelText="Cancel"
                  okText="CONFIRM USE TICKET"
                  onOk={async () => {
                    await handleScanTicket([ticket.id]);
                    setConfirmDialogVisible(false);
                  }}
                  loading={isLoading}
                >
                  <Text category="h6" status="basic">
                    Are you sure you want to use this ticket?
                  </Text>
                  <Spacer />
                  <Text status="basic">
                    This action should only be performed by arena staff at the
                    time of entry.
                  </Text>
                </Dialog>
              </>
            </View>
          ) : (
            <Image
              style={{ width: 150, height: 150 }}
              source={{
                uri: generateBase64String(
                  ticketsWithBarcode[index]?.barcodeBase64Png
                ),
              }}
            ></Image>
          )}
        </View>
        <View style={{ alignItems: "center", paddingVertical: 10 }}>
          {ticketFooterText && (
            <RenderHtmlComponent
              textColor="white"
              source={ticketFooterText}
              bodyStyle={{
                fontSize: 12,
                textAlign: "center",
                lineHeight: 14,
              }}
            ></RenderHtmlComponent>
          )}
          <View style={{ minHeight: 50 }}>
            {!tenantConfig?.hideTransferabilityText ? (
              <>
                {ticket.transferability === "NONE" && (
                  <Text style={styles.smallText}>
                    {i18n.t("screens.Barcode.reservationsNonTransferable")}
                  </Text>
                )}
              </>
            ) : null}

            <Text style={styles.smallText}>
              {i18n.t("screens.Barcode.animationRequired") as string}.
            </Text>
            <Text style={styles.smallText}>
              {i18n.t("screens.Barcode.screenshotNotAccepted") as string}.
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  lightText: { color: "rgba(255,255,255,0.8)" },
  boldText: { fontSize: 20, fontWeight: "700" },
  smallText: { fontSize: 12, letterSpacing: 1 },
});

export default BarcodeRenderItem;
